import React, { useState, useEffect, KeyboardEvent, useMemo, useCallback, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { RefreshProjectProps } from "../../types/screensTypes";
import * as Yup from "yup";
import { getWaitingAudioAI, createTextQueueAI, createProjectAI, addItemToProjectAI, getAdminInfo } from "../../api";
import { getBrandVoices, BrandVoice } from "../../api/brandVoice";
import AddToProjectModal from "../../components/modals/AddToProjectModal";
import CreateBrandVoiceModal from "../../components/modals/CreateBrandVoiceModal";
import { useGlobalState } from "../../context/useGlobalState";
import { User } from "../../types/types";
import Button from "../../components/ui/Button";
import TextInput from "../../components/ui/TextInput";
import AddToProjectButton from "../../components/ui/AddToProjectButton";
import { Tooltip } from "@mui/material";
import AIGenerationStatusModal from "../../components/modals/AIGenerationStatusModal";
import useCancelGeneration from "../../hooks/useCancelGeneration";
import Tag from "../../components/common/Tag";
import Icon from "../../components/common/Icon";
import styles from "./TextCreation.module.css";
import { FastField, Field, Formik, useFormikContext } from "formik";
import {
    DatePickerField,
    DropdownField,
    MultiSelectDropdownField,
    NumberField,
    TextField,
} from "../../components/common/FormFields";
import FormGroup from "../../components/common/FormGroup";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { AI_MODEL_TYPES } from "../../constants";
import { useRemainingTokens } from "../../hooks/useRemainingTokens";
import { isQuotaError } from "../../utils/quotaError";
import stepStyles from "../track/create/createStep.module.css";

interface PromotionalFormValues {
    selectedContentLanguage: string;
    textObject: string;
    textTitle: string;
    audioDuration: { minutes: number; seconds: number };
    audience: string;
    numberStories: number;
    requiredContent: string[];
    toneOfVoice: string;
    promotionStartDate?: Date;
    promotionEndDate?: Date;
    eventType: "ephemeral" | "permanent";
    cityId: string;
    brandVoiceId: string;
}

const PromotionalTextCreationForm: React.FC<{
    handleAudioAIAwaiting: (id: string) => Promise<void>;
    id?: string;
    user?: User | null;
    brandVoices: BrandVoice[];
    isCreateBrandVoiceModalOpen: boolean;
    onOpenCreateBrandVoiceModal: () => void;
    onCloseCreateBrandVoiceModal: () => void;
    onBrandVoiceCreated: (brandVoiceId: string) => void;
}> = ({
    handleAudioAIAwaiting,
    id,
    user,
    brandVoices,
    isCreateBrandVoiceModalOpen,
    onOpenCreateBrandVoiceModal,
    onCloseCreateBrandVoiceModal,
    onBrandVoiceCreated,
}) => {
    const { values, setFieldValue, resetForm } = useFormikContext<PromotionalFormValues>();

    const { numberStories, requiredContent, eventType, textObject, brandVoiceId } = values;

    const { t: rawT } = useTranslation();
    const t = rawT as any;
    const { data } = useGlobalState();

    const [contentInput, setContentInput] = useState<string>("");

    const EPHEMERAL_OPTIONS = useMemo(() => {
        const unsorted = [
            { value: "Festivals", label: t("festivals") },
            { value: "Exhibitions", label: t("exhibitions") },
            { value: "Concerts", label: t("concerts") },
            { value: "Open-air cinema", label: t("openAirCinema") },
            { value: "Seasonal markets", label: t("seasonalMarkets") },
            { value: "Pop-up events", label: t("popUpEvents") },
            { value: "Temporary installations", label: t("temporaryInstallations") },
            { value: "Parades", label: t("parades") },
            { value: "Cultural events", label: t("culturalEvents") },
            { value: "Sports competitions", label: t("sportsCompetitions") },
            { value: "Food festivals", label: t("foodFestivals") },
            { value: "Light shows or night events", label: t("lightShows") },
            { value: "Street performances or shows", label: t("streetPerformances") },
        ];
        return [...unsorted].sort((a, b) => a.label.localeCompare(b.label));
    }, [t]);

    const PERMANENT_OPTIONS = useMemo(() => {
        const unsorted = [
            { value: "Museums", label: t("museums") },
            { value: "Historical monuments", label: t("historicalMonuments") },
            { value: "Restaurants", label: t("restaurants") },
            { value: "Local markets", label: t("localMarkets") },
            { value: "Cruises", label: t("cruises") },
            { value: "City tours", label: t("cityTours") },
            { value: "Wine or food tastings", label: t("wineOrFoodTastings") },
            { value: "Cooking classes", label: t("cookingClasses") },
            { value: "Artisan workshops & shops", label: t("artisanShops") },
            { value: "Shopping and stores", label: t("shopping") },
            { value: "Nature walks and trails", label: t("natureWalks") },
            { value: "Bike or scooter rentals", label: t("bikeRentals") },
            {
                value: "Wellness activities (for example yoga spa meditation baths sauna hammam)",
                label: t("wellnessActivities"),
            },
            {
                value: "Water sports (for example surfing paddleboarding kayaking windsurfing jet ski rentals)",
                label: t("waterSports"),
            },
            {
                value: "Outdoor sports (for example hiking mountain biking rock climbing tennis)",
                label: t("outdoorSports"),
            },
            { value: "Indoor sports (for example climbing fitness swimming squash tennis)", label: t("indoorSports") },
            { value: "Theme or amusement parks", label: t("themeParks") },
            { value: "Aquariums and zoos", label: t("aquariumsAndZoos") },
            { value: "Family-friendly activities", label: t("familyActivities") },
        ];
        return [...unsorted].sort((a, b) => a.label.localeCompare(b.label));
    }, [t]);

    const experienceOptions = useMemo(() => {
        return eventType === "permanent" ? PERMANENT_OPTIONS : EPHEMERAL_OPTIONS;
    }, [eventType, PERMANENT_OPTIONS, EPHEMERAL_OPTIONS]);

    const sortedCityOptions = useMemo(() => {
        if (!data?.locationCityOptions) return [];
        return [...data.locationCityOptions].sort((a, b) => a.label.localeCompare(b.label));
    }, [data?.locationCityOptions]);

    const sortedLanguages = useMemo(() => {
        if (!data?.languagesAI) return [];
        return [...data.languagesAI].sort((a, b) => a.label.localeCompare(b.label));
    }, [data?.languagesAI]);

    /**
     * Handles adding a new required content item when pressing Enter
     */
    const handleContentKeyDown = (e: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        if (e.key === "Enter" && contentInput.trim() !== "") {
            if (requiredContent.length < numberStories) {
                if (contentInput.trim().length <= 80) {
                    setFieldValue("requiredContent", [...requiredContent, contentInput.trim()]);
                }
            }
            e.preventDefault();
        }
    };

    /**
     * Removes a content item from the list
     */
    const handleRemoveContent = (index: number) => {
        const updatedContent = [...requiredContent];
        updatedContent.splice(index, 1);
        setFieldValue("requiredContent", updatedContent);
    };

    const hasRequiredContentError = useMemo(
        () => requiredContent.length > numberStories,
        [requiredContent, numberStories],
    );

    useEffect(() => {
        if (id && id.trim() !== "") {
            handleAudioAIAwaiting(id);
        } else {
            resetForm();
        }
    }, [id, handleAudioAIAwaiting, resetForm]);

    return (
        <div className={stepStyles.stepDescription}>
            {/* Language */}
            <FormGroup
                name="selectedContentLanguage"
                title={t("language")}
                style={{ marginTop: 0, maxWidth: "220px" }}
                size="small"
                required
            >
                <Field
                    name="selectedContentLanguage"
                    label={t("textCreationLanguageHelpText")}
                    component={DropdownField}
                    options={sortedLanguages}
                    loading={false}
                    error={false}
                    placeholder={t("selectALanguage")}
                    isWithoutMargin={true}
                />
            </FormGroup>

            <FormGroup
                name="numberStories"
                title={t("numberStories")}
                subtitle={t("numberStoriesHelptext")}
                tooltip={t("numberStoriesTooltip")}
                style={{ marginTop: 20, maxWidth: "220px" }}
                size="small"
                required
            >
                <FastField component={NumberField} name="numberStories" min={1} max={20} />
            </FormGroup>

            {/* City */}
            <FormGroup
                name="cityId"
                title={t("city")}
                style={{ marginTop: 20, maxWidth: "220px" }}
                size="small"
                required
            >
                <FastField
                    name="cityId"
                    label={t("cityPromotionHelpText")}
                    component={DropdownField}
                    options={sortedCityOptions}
                    loading={false}
                    error={false}
                    placeholder={t("selectACity")}
                    isWithoutMargin={true}
                />
            </FormGroup>

            {/* EventType */}
            <FormGroup
                name="eventType"
                title={t("eventType")}
                style={{ marginTop: 20, maxWidth: "220px" }}
                size="small"
                required
            >
                <FastField
                    name="eventType"
                    label={t("eventTypeHelpText")}
                    component={DropdownField}
                    options={[
                        { value: "ephemeral", label: t("ephemeral") },
                        { value: "permanent", label: t("permanent") },
                    ]}
                    loading={false}
                    error={false}
                    placeholder={t("selectATypeOfEvent")}
                    isWithoutMargin={true}
                />
            </FormGroup>

            {/* Promotion period */}
            {eventType === "ephemeral" && (
                <FormGroup
                    title={t("promotionPeriod")}
                    tooltip={t("promotionPeriodTooltip")}
                    style={{ marginTop: 20 }}
                    required={true}
                    size="small"
                >
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <div className="promotion-period-container">
                            <FastField
                                name="promotionStartDate"
                                label={t("startDate")}
                                placeholder={t("startDate")}
                                errorMessage={t("invalidDate")}
                                component={DatePickerField}
                            />
                            <FastField
                                name="promotionEndDate"
                                label={t("endDate")}
                                placeholder={t("endDate")}
                                minDate={values.promotionStartDate}
                                errorMessage={t("invalidDate")}
                                component={DatePickerField}
                            />
                        </div>
                    </LocalizationProvider>
                </FormGroup>
            )}

            <FormGroup
                name="textTitle"
                title={t("title")}
                style={{ marginTop: 20, maxWidth: "220px" }}
                size="small"
                required
                tooltip={t("textTitleTooltip")}
            >
                <FastField
                    name="textTitle"
                    component={TextField}
                    placeholder={t("titleOfTheText")}
                    size={"small"}
                    style={{ marginTop: "8px" }}
                />
            </FormGroup>

            {/* Topic - Multi-select for promotion */}
            <FormGroup
                name="textObject"
                title={t("topic")}
                subtitle={t("whatIsGeneralTopicPromotion")}
                style={{ marginTop: 20, maxWidth: "220px" }}
                size="small"
                required
            >
                <Field
                    name="textObject"
                    component={MultiSelectDropdownField}
                    placeholder={t("selectOptions")}
                    options={experienceOptions}
                    onChange={(selectedValues: string[]) => {
                        // Convert array → comma-separated string
                        setFieldValue("textObject", selectedValues.join(", "));
                    }}
                    // Convert initial string value → array for display
                    value={(textObject || "")
                        .split(",")
                        .map((v: string) => v.trim())
                        .filter(Boolean)}
                    isWithoutMargin
                />
            </FormGroup>

            <>
                <h3 style={{ marginTop: "20px" }}>{t ? t("requiredSpecificContent") : "Required specific content"}</h3>
                <div style={{ width: "100%", marginTop: "0px" }}>
                    <div className="dropdown">
                        <label>
                            <span>
                                {t("specifyRequiredContentLabel")}
                                <span style={{ marginLeft: "5px", verticalAlign: "middle" }}>
                                    <Tooltip title={t("requiredContentInfoPromotion")} arrow placement="top">
                                        <span style={{ width: "18px", height: "18px", display: "inline-block" }}>
                                            ℹ️
                                        </span>
                                    </Tooltip>
                                </span>
                            </span>
                        </label>
                        <div style={{ marginTop: "5px" }}>
                            <Tooltip
                                title={
                                    requiredContent.length >= numberStories
                                        ? t
                                            ? t("maxContentItemsReached")
                                            : `Maximum of ${numberStories} content items reached`
                                        : ""
                                }
                                arrow
                                placement="top"
                            >
                                <div
                                    style={{
                                        cursor: requiredContent.length >= numberStories ? "not-allowed" : "auto",
                                    }}
                                >
                                    <div style={{ display: "flex", alignItems: "center", marginBottom: "5px" }}>
                                        <span style={{ fontSize: "12px", color: "#666", fontStyle: "italic" }}>
                                            {t ? t("pressEnterToAddContent") : "Press Enter to add content"}
                                        </span>
                                        <button
                                            style={{
                                                marginLeft: "10px",
                                                padding: "2px 8px",
                                                backgroundColor: "var(--green-600)",
                                                color: "white",
                                                border: "none",
                                                borderRadius: "4px",
                                                cursor:
                                                    requiredContent.length >= numberStories ||
                                                    !contentInput.trim() ||
                                                    contentInput.length > 80
                                                        ? "not-allowed"
                                                        : "pointer",
                                                opacity:
                                                    requiredContent.length >= numberStories ||
                                                    !contentInput.trim() ||
                                                    contentInput.length > 80
                                                        ? 0.6
                                                        : 1,
                                            }}
                                            onClick={() => {
                                                if (
                                                    contentInput.trim() !== "" &&
                                                    requiredContent.length < numberStories &&
                                                    contentInput.length <= 80
                                                ) {
                                                    setFieldValue("requiredContent", [
                                                        ...requiredContent,
                                                        contentInput.trim(),
                                                    ]);
                                                    setContentInput("");
                                                }
                                            }}
                                            disabled={
                                                requiredContent.length >= numberStories ||
                                                !contentInput.trim() ||
                                                contentInput.length > 80
                                            }
                                        >
                                            {t ? t("add") : "Add"}
                                        </button>
                                    </div>
                                    <TextInput
                                        placeholder={t("specifyRequiredContentExamplePromotion")}
                                        value={contentInput}
                                        size={"small"}
                                        onChange={(e) => setContentInput(e.target.value)}
                                        onKeyDown={handleContentKeyDown}
                                        disabled={requiredContent.length >= numberStories}
                                    />
                                    {contentInput.length > 80 && (
                                        <div className="content-warning">
                                            {t
                                                ? t("contentItemTooLong", { count: contentInput.length })
                                                : `Content item must be 80 characters or less (currently ${contentInput.length})`}
                                        </div>
                                    )}

                                    <div className="content-char-counter">
                                        {contentInput.length}/80 {t ? t("charactersLimit") : "characters"}
                                    </div>
                                </div>
                            </Tooltip>
                        </div>
                    </div>
                    <div style={{ marginTop: "10px" }}>
                        {requiredContent.map((item, index) => (
                            <Tag
                                key={`content-${index}`}
                                text={item}
                                onRemove={() => handleRemoveContent(index)}
                                backgroundColor={hasRequiredContentError ? "#ff0000" : "var(--green-600)"}
                            />
                        ))}
                    </div>
                    <div
                        style={{
                            marginTop: "5px",
                            fontSize: "12px",
                            color: hasRequiredContentError ? "#ff0000" : "#213137",
                        }}
                    >
                        {requiredContent.length}/{numberStories} {t ? t("specified") : "specified"}
                        {hasRequiredContentError && (
                            <div style={{ color: "#ff0000", fontSize: "12px", marginTop: "3px" }}>
                                {t ? t("tooManyRequiredContentItems") : "Too many content items specified"}
                            </div>
                        )}
                    </div>
                </div>
            </>

            {/* Audio Duration */}
            <FormGroup
                name="audioDuration"
                title={t("audioDuration")}
                subtitle={t("audioDurationHelptext")}
                style={{ marginTop: 20, maxWidth: "220px" }}
                size="small"
                required
            >
                <div style={{ marginTop: "5px", display: "flex", gap: "10px", alignItems: "center" }}>
                    <Field name="audioDuration.minutes" component={NumberField} placeholder="minutes" />
                    <span style={{ fontSize: "13px" }}>min</span>
                    <Field name="audioDuration.seconds" component={NumberField} placeholder="seconds" />
                    <span style={{ fontSize: "13px" }}>{t("seconds")}</span>
                </div>
            </FormGroup>

            {/* Audience */}
            <FormGroup
                name="audience"
                title={t("audience")}
                subtitle={t("whoIsYourContentFor")}
                style={{ marginTop: 20, maxWidth: "220px" }}
                size="small"
                required
            >
                <Field name="audience" component={TextField} placeholder={t("whoIsYourContentFor")} />
            </FormGroup>

            {/* Writing Style (formerly Brand Voice) */}
            <FormGroup
                name="brandVoiceId"
                title={t("brandVoice")}
                subtitle={t("brandVoiceHelpText")}
                style={{ marginTop: 20, maxWidth: "220px" }}
                size="small"
            >
                <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                    <Field
                        name="brandVoiceId"
                        component={DropdownField}
                        label={t("selectBrandVoice")}
                        options={[
                            { value: "__none__", label: t("noVoice") },
                            ...brandVoices.map((bv) => ({
                                value: bv.id,
                                label: bv.name,
                            })),
                            ...(brandVoices.length < 10
                                ? [{ value: "create_new", label: t("createNewBrandVoice") || "Create New..." }]
                                : []),
                        ]}
                        error={false}
                        placeholder={t("noVoice")}
                        isWithoutMargin={true}
                        onChange={(value: string) => {
                            if (value === "create_new") {
                                onOpenCreateBrandVoiceModal();
                                setFieldValue("brandVoiceId", "");
                            } else {
                                setFieldValue("brandVoiceId", !value || value === "__none__" ? "" : value);
                            }
                        }}
                    />
                    {brandVoiceId && brandVoiceId !== "" && (
                        <div style={{ fontSize: "12px", color: "#666", fontStyle: "italic" }}>
                            {t("brandVoiceSelected")}: {brandVoices.find((bv) => bv.id === brandVoiceId)?.name}
                        </div>
                    )}
                </div>
            </FormGroup>
        </div>
    );
};

const PromotionalTextCreation: React.FC<RefreshProjectProps> = ({ refreshProjects }) => {
    const { t: rawT, i18n } = useTranslation();

    const t = rawT as any;
    const { id } = useParams<{ id?: string }>();
    const navigate = useNavigate();

    const [user, setUser] = useState<any>(null);
    const [textGenerated, setTextGenerated] = useState("");
    const [loadingTextGeneration, setLoadingTextGeneration] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const [showStatusModal, setShowStatusModal] = useState(false);
    const [waitingRank, setWaitingRank] = useState<number | null>(null);
    const [generationStatus, setGenerationStatus] = useState<"waiting" | "success" | "error">("waiting");
    const [errorReason, setErrorReason] = useState<"generic" | "quota">("generic");
    const [isInProject, setIsInProject] = useState<boolean>(false);
    const [brandVoices, setBrandVoices] = useState<BrandVoice[]>([]);
    const [isCreateBrandVoiceModalOpen, setIsCreateBrandVoiceModalOpen] = useState(false);
    const setFieldValueRef = useRef<((field: string, value: any) => void) | null>(null);
    /**
     * Generation launched from this page, until its outcome is shown. Opening a text any other
     * way (drafts, browser back) must not pop the status modal for a generation long over.
     */
    const launchedIdRef = useRef<string | null>(null);
    const { isTextExhausted, refetch: refetchTokens } = useRemainingTokens();
    const { cancelling, cancelGeneration, confirmCanceled, resetCancelling } = useCancelGeneration();

    useEffect(() => {
        const fetchUser = async () => {
            const token = localStorage.getItem("token");
            if (!token) return;
            try {
                const response = await getAdminInfo({ token });
                setUser(response);
            } catch (err) {
                console.error(err);
            }
        };

        fetchUser();
    }, []);

    useEffect(() => {
        const fetchBrandVoices = async () => {
            try {
                const voices = await getBrandVoices();
                setBrandVoices(voices);
            } catch (err) {
                console.error("Error fetching brand voices:", err);
            }
        };

        fetchBrandVoices();
    }, []);

    const handleAudioAIAwaiting = useCallback(
        async (id: string) => {
            // The modal and the outcome (success, failure, canceled) only concern a generation
            // launched from this page, or one still running when the page opens.
            let showOutcome = launchedIdRef.current === id;
            const concludeLaunch = () => {
                if (launchedIdRef.current === id) launchedIdRef.current = null;
            };

            setLoadingTextGeneration(true);
            setShowStatusModal(showOutcome);
            setGenerationStatus("waiting");
            setWaitingRank(null);

            const fetchAudioAI = async () => {
                try {
                    const audioAI = await getWaitingAudioAI({
                        token: localStorage.getItem("token") || "",
                        id,
                    });

                    if (audioAI?.failed) {
                        setLoadingTextGeneration(false);
                        setGenerationStatus("error");
                        resetCancelling();
                        concludeLaunch();
                        return;
                    }

                    // The form is still filled in: closing the modal is enough to relaunch.
                    if (audioAI?.canceled) {
                        setLoadingTextGeneration(false);
                        setShowStatusModal(false);
                        setWaitingRank(null);
                        if (showOutcome) confirmCanceled();
                        else resetCancelling();
                        concludeLaunch();
                        return;
                    }

                    if (audioAI?.waiting) {
                        // Still running: from here on, its outcome is worth showing.
                        if (!showOutcome) {
                            showOutcome = true;
                            setShowStatusModal(true);
                        }
                        if (typeof audioAI.rank === "number") {
                            setWaitingRank(audioAI.rank);
                        }
                        setTimeout(fetchAudioAI, 5000);
                    } else {
                        setLoadingTextGeneration(false);
                        if (audioAI?.audioAI) {
                            if (audioAI.audioAI.text === "Nothing found") {
                                setTextGenerated(t("nothingFoundText"));
                            } else {
                                setTextGenerated(audioAI.audioAI.text);
                            }
                            // Check if audioAI is already in a project
                            setIsInProject(!!audioAI.audioAI.projectAIId);
                            setGenerationStatus("success");
                            refetchTokens();
                        }
                        setWaitingRank(null);
                        resetCancelling();
                        concludeLaunch();
                    }
                } catch (error) {
                    console.error("Error fetching audio AI data:", error);
                    setLoadingTextGeneration(false);
                    // A text that cannot be read is worth reporting however the page was opened.
                    setShowStatusModal(true);
                    setGenerationStatus("error");
                    resetCancelling();
                    concludeLaunch();
                }
            };

            fetchAudioAI();
        },
        [
            setLoadingTextGeneration,
            setShowStatusModal,
            setGenerationStatus,
            setWaitingRank,
            setTextGenerated,
            t,
            refetchTokens,
            confirmCanceled,
            resetCancelling,
        ],
    );

    const validationSchema = Yup.object({
        selectedContentLanguage: Yup.string().required(t("languageRequired")),
        textObject: Yup.string().required(t("topicRequired")),
        textTitle: Yup.string().required(t("titleRequired")),
        audioDuration: Yup.object({
            minutes: Yup.number().min(0),
            seconds: Yup.number().min(0),
        }).test("duration", t("audioDurationRequired"), (value) => {
            return (value?.minutes || 0) + (value?.seconds || 0) > 0;
        }),
        audience: Yup.string().required(t("audienceRequired")),
        numberStories: Yup.number().min(1).max(20),
        requiredContent: Yup.array().of(Yup.string().max(80)),
        toneOfVoice: Yup.string(),
        eventType: Yup.mixed<"ephemeral" | "permanent">().required(t("eventTypeRequired")),
        promotionStartDate: Yup.date().when("eventType", {
            is: "ephemeral",
            then: (schema) => schema.required(t("startDateRequired")),
            otherwise: (schema) => schema.notRequired(),
        }),
        promotionEndDate: Yup.date().when(["eventType", "promotionStartDate"], {
            is: (eventType: string) => eventType === "ephemeral",
            then: (schema) =>
                schema.required(t("endDateRequired")).min(Yup.ref("promotionStartDate"), t("endDateAfterStartDate")),
            otherwise: (schema) => schema.notRequired(),
        }),
        cityId: Yup.string().required(t("cityRequired")),
    });

    const initialValues: PromotionalFormValues = {
        selectedContentLanguage: "",
        textObject: "",
        textTitle: "",
        audioDuration: { minutes: 0, seconds: 0 },
        audience: "",
        numberStories: 1,
        requiredContent: [],
        toneOfVoice: "",
        promotionStartDate: new Date(), // today
        promotionEndDate: new Date(new Date().setDate(new Date().getDate() + 7)), // one week later
        eventType: "ephemeral",
        cityId: "",
        brandVoiceId: "",
    };

    const handleSubmit = async (values: PromotionalFormValues) => {
        setLoadingTextGeneration(true);
        setShowStatusModal(true);
        setGenerationStatus("waiting");
        setErrorReason("generic");

        try {
            const response = await createTextQueueAI({
                token: localStorage.getItem("token") || "",
                isFromDb: false, // Promotion always uses general knowledge
                language: values.selectedContentLanguage,
                textObject: values.textObject,
                audioDurationMin: values.audioDuration.minutes,
                audioDurationSec: values.audioDuration.seconds,
                audience: values.audience,
                hasToneOfVoice: !!values.toneOfVoice,
                toneOfVoice: values.toneOfVoice,
                textTitle: values.textTitle,
                numberStories: values.numberStories,
                requiredContent: values.requiredContent,
                textType: "promotion",
                startDate: values?.promotionStartDate?.toISOString(),
                endDate: values?.promotionEndDate?.toISOString(),
                eventType: values.eventType,
                cityId: values.cityId,
                queryFilters: undefined,
                restrictToDatabase: false,
                brandVoiceId: values.brandVoiceId && values.brandVoiceId !== "" ? values.brandVoiceId : null,
            });

            if (response?.id) {
                launchedIdRef.current = response.id;
                navigate(`/create/promotional-text/${response.id}`);
            } else {
                setGenerationStatus("error");
            }
        } catch (err) {
            if (isQuotaError(err)) {
                setErrorReason("quota");
                refetchTokens();
            }
            setGenerationStatus("error");
        } finally {
            setLoadingTextGeneration(false);
        }
    };

    const handleNextStep = () => {
        navigate(`/create/text-remix/${id || ""}`);
    };

    const handleAddToProject = async (projectId: string, newProjectName: string) => {
        const token = localStorage.getItem("token");
        if (!token || !id) {
            return;
        }

        const audioAI = await getWaitingAudioAI({ token: token, id: id });

        if (audioAI?.audioAI) {
            if (projectId === "new") {
                await createProjectAI({
                    token: token,
                    name: newProjectName,
                    audioAIId: audioAI.audioAI.id,
                });
            } else {
                await addItemToProjectAI({
                    token: token,
                    projectId: projectId,
                    itemId: audioAI.audioAI.id,
                    itemType: AI_MODEL_TYPES.AUDIO,
                });
            }

            await refreshProjects();
            // Refresh the audioAI to check if it's now in a project
            if (id) {
                const updatedAudioAI = await getWaitingAudioAI({ token: token, id: id });
                if (updatedAudioAI?.audioAI) {
                    setIsInProject(!!updatedAudioAI.audioAI.projectAIId);
                }
            }
        }
    };

    return (
        <div className={styles.page}>
            <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
                key={i18n.language}
                validateOnMount
            >
                {({ isSubmitting, submitForm, errors, values: { textTitle }, setFieldValue }) => {
                    // Store setFieldValue in ref for use in modal callback
                    setFieldValueRef.current = setFieldValue;

                    return (
                        <>
                            <div className={styles.work}>
                                <section className={styles.editor}>
                                    <div className={styles.editorHead}>
                                        <div>
                                            <h1>{t("textPrompt")}</h1>
                                            <p>{t("fillThisSectionToGenerateYourStoryYouCanEditItManuallyLater")}</p>
                                        </div>
                                    </div>
                                    <div className={styles.canvas}>
                                        {!textGenerated && !loadingTextGeneration ? (
                                            <div className={styles.canvasEmpty}>
                                                <div className={styles.empty}>
                                                    <div className={styles.emptyIco}>
                                                        <Icon name="ic-file-text" />
                                                    </div>
                                                    <h3>{t("yourTextWillAppearHere")}</h3>
                                                    <p>{t("yourTextWillAppearHereWhenGenerationIsDone")}</p>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className={styles.canvasEditor}>
                                                <textarea
                                                    className="text-input-big"
                                                    placeholder={
                                                        loadingTextGeneration
                                                            ? t("pleaseWaitTextGenerationInProgress")
                                                            : t("yourTextWillAppearHereWhenGenerationIsDone")
                                                    }
                                                    value={loadingTextGeneration ? "" : textGenerated}
                                                    rows={12}
                                                    disabled={false}
                                                    onChange={(e) => setTextGenerated(e.target.value)}
                                                />
                                            </div>
                                        )}
                                    </div>
                                </section>

                                <aside className={styles.params}>
                                    <div className={styles.paramsHead}>
                                        <Icon name="ic-settings" />
                                        {t("settings")}
                                    </div>
                                    <div className={styles.paramsBody}>
                                        <PromotionalTextCreationForm
                                            handleAudioAIAwaiting={handleAudioAIAwaiting}
                                            id={id}
                                            user={user}
                                            brandVoices={brandVoices}
                                            isCreateBrandVoiceModalOpen={isCreateBrandVoiceModalOpen}
                                            onOpenCreateBrandVoiceModal={() => setIsCreateBrandVoiceModalOpen(true)}
                                            onCloseCreateBrandVoiceModal={() => setIsCreateBrandVoiceModalOpen(false)}
                                            onBrandVoiceCreated={async () => {
                                                const voices = await getBrandVoices();
                                                setBrandVoices(voices);
                                            }}
                                        />
                                    </div>
                                    <div className={styles.paramsFoot}>
                                        <Tooltip
                                            title={
                                                isTextExhausted
                                                    ? t("quotaReachedTextTooltip")
                                                    : Object.keys(errors).length > 0
                                                      ? t("formIncompleteTooltip", {
                                                            fields: Object.values(errors).join(", "),
                                                        })
                                                      : ""
                                            }
                                            placement="top"
                                        >
                                            <div>
                                                <Button
                                                    label={
                                                        loadingTextGeneration
                                                            ? waitingRank !== null && waitingRank > 0
                                                                ? t("waitingInQueue", { rank: waitingRank })
                                                                : t("generating")
                                                            : t("generateText")
                                                    }
                                                    onClick={submitForm}
                                                    disabled={
                                                        isSubmitting ||
                                                        Object.keys(errors).length > 0 ||
                                                        isTextExhausted
                                                    }
                                                    variant="primary"
                                                    shape="default"
                                                    block
                                                    className={styles.generateBtn}
                                                    icon={
                                                        !loadingTextGeneration ? (
                                                            <Icon name="ic-sparkles" className="ic" />
                                                        ) : undefined
                                                    }
                                                />
                                            </div>
                                        </Tooltip>
                                        {textGenerated && (
                                            <Button
                                                label={t("nextStep")}
                                                onClick={handleNextStep}
                                                variant="outline"
                                                shape="pill"
                                            />
                                        )}
                                        {textGenerated &&
                                            textGenerated !== "" &&
                                            textTitle &&
                                            textTitle !== "" &&
                                            !isInProject && <AddToProjectButton onClick={() => setIsModalOpen(true)} />}
                                    </div>
                                </aside>
                            </div>
                            <AddToProjectModal
                                isOpen={isModalOpen}
                                onClose={() => setIsModalOpen(false)}
                                onAddToProject={handleAddToProject}
                            />
                            <AIGenerationStatusModal
                                isOpen={showStatusModal}
                                onClose={() => setShowStatusModal(false)}
                                status={generationStatus}
                                rank={waitingRank}
                                errorTitle={errorReason === "quota" ? t("quotaReachedTitle") : undefined}
                                errorSubtitle={errorReason === "quota" ? t("quotaReachedTextSubtitle") : undefined}
                                onCancel={id ? () => cancelGeneration(id) : undefined}
                                cancelling={cancelling}
                            />
                            <CreateBrandVoiceModal
                                isOpen={isCreateBrandVoiceModalOpen}
                                onClose={() => setIsCreateBrandVoiceModalOpen(false)}
                                onSuccess={async (brandVoiceId: string) => {
                                    const voices = await getBrandVoices();
                                    setBrandVoices(voices);
                                    if (setFieldValueRef.current) {
                                        setFieldValueRef.current("brandVoiceId", brandVoiceId || "");
                                    }
                                }}
                            />
                        </>
                    );
                }}
            </Formik>
        </div>
    );
};

export default PromotionalTextCreation;
